import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Check, X } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GuideLinks } from "@/components/GuideLinks";
import { LeadForm } from "@/components/LeadForm";
import { StickyCta } from "@/components/StickyCta";
import { CaseStrip } from "@/components/CasePreview";

const SITE = "https://shopify-freelancer.net";
const PATH = "/shopify-seo";
const TITLE = "Shopify SEO: Freelancer statt Agentur | Nicolai Schnepf";
const DESCRIPTION =
  "Shopify SEO und Suchmaschinenoptimierung für Onlineshops: Kategorieseiten, Varianten, Weiterleitungen, Ladezeit, strukturierte Daten und KI-Suche. 110 € netto pro Stunde, größere Vorhaben zum Festpreis.";

const faqs = [
  {
    q: "Was kostet Shopify SEO bei Ihnen?",
    a: "110 € netto pro Stunde nach tatsächlichem Aufwand. Für abgegrenzte Vorhaben, etwa eine technische Prüfung mit Umsetzungsplan oder den Umbau der Kategoriestruktur, bekommen Sie vorab einen Festpreis. Eine erste Prüfung liegt erfahrungsgemäß bei 990 bis 1.900 € netto, je nach Größe des Sortiments.",
  },
  {
    q: "Was kostet eine Shopify SEO Agentur im Vergleich?",
    a: "Agenturen arbeiten üblicherweise mit monatlichen Betreuungspaketen ab etwa 1.500 € netto und Laufzeiten von sechs bis zwölf Monaten. Bei mir gibt es keine Mindestlaufzeit und keinen Aufschlag für Projektleitung, dafür arbeite ich als Einzelperson und bin nicht für beliebig viele Stunden im Monat verfügbar.",
  },
  {
    q: "Wie lange dauert es, bis SEO wirkt?",
    a: "Technische Korrekturen wie Weiterleitungen, Canonicals oder Ladezeit greifen oft innerhalb weniger Wochen. Rankings für umkämpfte Suchbegriffe brauchen deutlich länger und hängen von Wettbewerb, Inhalten und Verlinkung ab. Zusagen zu Positionen gibt es von mir nicht.",
  },
  {
    q: "Ist Shopify gut für SEO?",
    a: "Die technischen Grundlagen sind solide: saubere URLs, automatische Sitemap, schnelle Server, HTTPS und mobile Themes. Die typischen Probleme entstehen im Shop selbst: dünne Kategorieseiten, doppelte Varianten- und Filter-URLs, zu viele Apps im Quelltext und fehlende Weiterleitungen nach einer Migration.",
  },
  {
    q: "Welche Nachteile hat Shopify bei SEO?",
    a: "Die Verzeichnisstruktur ist fest vorgegeben, Produkte liegen zusätzlich unter Kategoriepfaden und brauchen deshalb saubere Canonicals. Die robots.txt ließ sich lange nicht ändern und ist erst über eine eigene Vorlage anpassbar. Blogfunktionen sind einfacher als bei WordPress. Keiner dieser Punkte verhindert gute Platzierungen, sie erfordern nur Sorgfalt bei der Einrichtung.",
  },
  {
    q: "Brauche ich eine SEO App für Shopify?",
    a: "Meistens nicht. Titel, Beschreibungen, Weiterleitungen und strukturierte Daten lassen sich direkt im Theme und in den Shop-Einstellungen lösen. Apps fügen zusätzliche Skripte ein und kosten Ladezeit. Sinnvoll sind sie bei sehr großen Sortimenten, wenn Textbausteine für Tausende Produkte gepflegt werden müssen.",
  },
  {
    q: "Machen Sie auch Inhalte und Texte?",
    a: "Ich lege Struktur, Themen und die technische Umsetzung fest und schreibe Kategorie- und Ratgebertexte, wenn Sie das möchten. Fachliche Details zu Ihren Produkten kommen von Ihnen, sonst wird der Text austauschbar.",
  },
  {
    q: "Was passiert bei einer Migration mit den Rankings?",
    a: "Entscheidend sind vollständige Weiterleitungen der alten Adressen auf die passenden neuen Seiten. Fehlen sie, verlieren Sie Sichtbarkeit, die über Jahre entstanden ist. Das gehört bei mir zu jedem Umzug dazu.",
  },
  {
    q: "Wie wird Shopify in ChatGPT und Perplexity gefunden?",
    a: "KI-Systeme zitieren Seiten, die eine Frage kurz und eindeutig beantworten. Hilfreich sind klare Überschriften, Fakten in Listen statt in Werbetexten, strukturierte Daten zu Produkten und Preisen sowie eine maschinenlesbare Kurzfassung. Das ersetzt klassische Suchmaschinenoptimierung nicht, sondern baut darauf auf.",
  },
  {
    q: "Arbeiten Sie auch an bestehenden Shops mit viel Traffic?",
    a: "Ja. Gearbeitet wird in einer Theme-Kopie, veröffentlicht wird nach Ihrer Freigabe und in kleinen Schritten, damit Änderungen einzeln auswertbar bleiben.",
  },
  {
    q: "Betreuen Sie Shops laufend oder nur projektweise?",
    a: "Beides ist möglich. Viele starten mit einer Prüfung und Umsetzung und buchen danach ein festes Stundenkontingent pro Monat für Auswertung, Inhalte und Nachbesserungen.",
  },
  {
    q: "Sind Sie der offizielle Shopify Support?",
    a: "Nein. Ich bin ein unabhängiger Freelancer und gehöre nicht zu Shopify. Fragen zu Abrechnung, Konto oder Störungen der Plattform beantwortet der Support unter help.shopify.com.",
  },
];

export const Route = createFileRoute("/shopify-seo")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Shopify SEO",
          alternateName: ["Suchmaschinenoptimierung Onlineshop", "Shopify SEO Betreuung"],
          description: DESCRIPTION,
          serviceType: "Shopify Suchmaschinenoptimierung",
          areaServed: ["DE", "AT", "CH"],
          provider: { "@type": "Person", name: "Nicolai Schnepf", url: SITE },
          offers: {
            "@type": "Offer",
            priceCurrency: "EUR",
            price: "110",
            description: "110 € netto pro Stunde, größere Vorhaben zum Festpreis.",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Startseite", item: SITE },
            { "@type": "ListItem", position: 2, name: "Leistungen", item: `${SITE}/leistungen` },
            { "@type": "ListItem", position: 3, name: "Shopify SEO", item: `${SITE}${PATH}` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
});

const scope = [
  {
    title: "Technische Prüfung",
    text: "Indexierung, Canonicals, Varianten-URLs, Weiterleitungen, Paginierung und Ladezeit. Zuerst wird repariert, was Sichtbarkeit aktiv kostet.",
  },
  {
    title: "Kategorieseiten",
    text: "Kategorien sind bei Shopify die stärksten Seiten und meist die schwächsten Inhalte. Struktur, Filter, Titel und Texte werden auf die Suchbegriffe ausgerichtet, nach denen wirklich gesucht wird.",
  },
  {
    title: "Produktseiten",
    text: "Eindeutige Titel, sinnvolle Beschreibungen, Produktdaten im Quelltext. So werden Produkte für konkrete Marken- und Modellsuchen gefunden.",
  },
  {
    title: "Ladezeit und Technik im Theme",
    text: "Überflüssige Apps, blockierende Skripte und zu große Bilder bremsen Rankings und Verkäufe gleichermaßen. Das wird direkt im Theme behoben.",
  },
  {
    title: "Inhalte mit Kaufabsicht",
    text: "Ratgeber- und Vergleichsseiten zu den Fragen, die vor dem Kauf gestellt werden, statt Blogtexte ohne Bezug zum Sortiment.",
  },
  {
    title: "KI-Suche",
    text: "Klare Fakten, kurze Antworten, strukturierte Daten und eine maschinenlesbare Kurzfassung, damit Systeme wie ChatGPT oder Perplexity Ihren Shop überhaupt zitieren können.",
  },
];

const steps = [
  { no: "01", title: "Anfrage", text: "Sie schildern Shop und Ziel. Ich sehe mir Sichtbarkeit, Struktur und Technik an." },
  { no: "02", title: "Prüfung", text: "Sie bekommen eine Liste der Punkte, die Sichtbarkeit kosten, nach Wirkung und Aufwand sortiert." },
  { no: "03", title: "Umsetzung", text: "Gearbeitet wird in einer Theme-Kopie, veröffentlicht wird nach Ihrer Freigabe." },
  { no: "04", title: "Auswertung", text: "Entwicklung in Search Console und Umsatz je Seite, so lange, bis die Daten belastbar sind." },
];

const mistakes = [
  {
    title: "Kategorieseiten ohne Text",
    text: "Eine Kategorie mit 40 Produkten und zwei Sätzen Beschreibung hat für Google kaum Substanz. Genau diese Seiten sollen aber für die umsatzstärksten Suchbegriffe ranken.",
  },
  {
    title: "Varianten und Filter erzeugen doppelte Adressen",
    text: "Farb- und Größenvarianten sowie Filterkombinationen erzeugen viele Adressen mit fast gleichem Inhalt. Ohne saubere Canonicals verteilt sich die Kraft auf Dutzende Seiten.",
  },
  {
    title: "Produkte unter zwei Pfaden erreichbar",
    text: "Shopify liefert Produkte sowohl direkt als auch unter dem Kategoriepfad aus. Verlinken Sie intern immer denselben Pfad, sonst widersprechen sich interne Links und Canonical.",
  },
  {
    title: "Fehlende Weiterleitungen nach Relaunch oder Umzug",
    text: "Jede alte Adresse ohne Ziel ist verschenkte Sichtbarkeit. Nach einer Migration ist das der häufigste Grund für Einbrüche.",
  },
  {
    title: "Zu viele Apps im Quelltext",
    text: "Bewertungen, Popups, Upsell, Chat, Tracking: jede App lädt eigene Skripte. Das schlägt direkt auf Ladezeit und mobile Bewertung durch.",
  },
  {
    title: "Titel aus dem Theme-Standard",
    text: "Seitentitel wie Produktname plus Shopname sind austauschbar. Titel sollen die Suchanfrage aufgreifen und einen Grund zum Klicken geben.",
  },
  {
    title: "Ausverkaufte Produkte ohne Plan",
    text: "Dauerhaft ausverkaufte Artikel gehören weitergeleitet oder mit Alternativen versehen, sonst sammeln Sie Seiten, die niemand mehr braucht.",
  },
  {
    title: "Bilder ohne Komprimierung und ohne Alternativtext",
    text: "Große Produktbilder sind die häufigste Bremse auf Mobilgeräten und die einfachste Quelle für Sichtbarkeit in der Bildersuche.",
  },
  {
    title: "Blog ohne Bezug zum Sortiment",
    text: "Beiträge zu allgemeinen Themen bringen Besucher ohne Kaufabsicht. Nützlich sind Inhalte zu den Fragen, die vor dem Kauf gestellt werden.",
  },
  {
    title: "Mehrere Sprachen ohne hreflang",
    text: "Wer über Shopify Markets mehrere Länder bedient, braucht korrekte Sprachverweise. Sonst konkurrieren die eigenen Länderversionen miteinander.",
  },
];

const checklist = {
  Technik: [
    "Indexierbarkeit und robots.txt prüfen",
    "Canonicals für Varianten, Filter und Kategoriepfade",
    "Weiterleitungen aller alten Adressen",
    "Sitemap und Search Console sauber eingerichtet",
    "Paginierung großer Kategorien",
    "Ladezeit und Core Web Vitals auf Mobilgeräten",
  ],
  Inhalt: [
    "Suchbegriffe je Kategorie festgelegt",
    "Kategorietexte, die das Sortiment erklären",
    "Eindeutige Titel und Beschreibungen",
    "Produkttexte mit echten Details statt Herstellerfloskeln",
    "Interne Verlinkung von starken auf wichtige Seiten",
    "Ratgeberinhalte zu Fragen vor dem Kauf",
  ],
  Vertrauen: [
    "Strukturierte Daten für Produkte, Preise und Bewertungen",
    "Versand, Rückgabe und Kontakt klar auffindbar",
    "Impressum und rechtliche Seiten vollständig",
    "Echte Bewertungen statt gekaufter Sterne",
    "Marke außerhalb des Shops erwähnt",
    "Maschinenlesbare Kurzfassung für KI-Suche",
  ],
};

const packages = [
  {
    name: "SEO Prüfung",
    price: "ab 990 € netto",
    text: "Vollständige Durchsicht von Technik, Struktur und Inhalten. Sie erhalten eine nach Wirkung sortierte Liste mit Aufwandsschätzung je Punkt.",
    items: ["Technische Prüfung", "Suchbegriffe je Kategorie", "Wettbewerbsvergleich", "Umsetzungsplan"],
  },
  {
    name: "Umsetzung im Shop",
    price: "110 € netto pro Stunde",
    text: "Die Punkte aus der Prüfung werden direkt im Theme und im Shop umgesetzt, nicht als Empfehlung übergeben.",
    items: ["Weiterleitungen und Canonicals", "Kategorie- und Produktseiten", "Ladezeit im Theme", "Strukturierte Daten"],
    featured: true,
  },
  {
    name: "Laufende Betreuung",
    price: "ab 660 € netto pro Monat",
    text: "Festes Stundenkontingent für Auswertung, neue Inhalte und Nachbesserungen. Keine Mindestlaufzeit.",
    items: ["Monatliche Auswertung", "Neue Kategorie- und Ratgeberseiten", "Technische Kontrolle", "Absprache nach Bedarf"],
  },
];

const comparison = [
  { point: "Ansprechpartner", free: "Sie sprechen direkt mit der Person, die umsetzt", agency: "Projektleitung zwischen Ihnen und der Umsetzung" },
  { point: "Abrechnung", free: "110 € netto pro Stunde oder Festpreis", agency: "Monatspaket, oft ab 1.500 € netto" },
  { point: "Laufzeit", free: "Keine Mindestlaufzeit", agency: "Häufig sechs bis zwölf Monate" },
  { point: "Umsetzung im Theme", free: "Inklusive, es wird im Code gearbeitet", agency: "Oft getrennt beauftragt oder extern" },
  { point: "Kapazität", free: "Begrenzt, eine Person", agency: "Mehrere Personen parallel" },
];

function Page() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />

      <main id="top">
        <section className="veil border-b border-border/60 py-16 md:py-24">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 md:grid-cols-2 md:items-center">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.34em] text-primary">
                Shopify · SEO
              </p>
              <h1 className="mt-5 text-4xl font-bold leading-[1.05] md:text-6xl">
                Shopify SEO, das <span className="text-signal">Bestellungen</span> bringt,
                nicht nur Platzierungen.
              </h1>
              <p className="mt-6 max-w-xl text-lg text-muted-foreground">
                Suchmaschinenoptimierung für Ihren Onlineshop, direkt im Shopify Theme umgesetzt.
                Ich behebe zuerst das, was Sichtbarkeit aktiv kostet, und baue danach die Seiten
                aus, über die Kunden mit Kaufabsicht kommen. Freelancer statt Agentur, ohne
                Mindestlaufzeit.
              </p>
              <p className="mt-4 text-sm text-muted-foreground">
                110 € netto pro Stunde. Abgegrenzte Vorhaben zum Festpreis.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#anfrage"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Kostenlose Einschätzung erhalten <ArrowUpRight className="h-4 w-4" />
                </a>
                <Link
                  to="/blog/shopify-seo-optimierung-guide"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold transition-colors hover:border-primary"
                >
                  Ratgeber Shopify SEO
                </Link>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Unverbindlich · Antwort innerhalb von 48 Stunden
              </p>
            </div>
            <div id="anfrage">
              <LeadForm
                source="lp-shopify-seo"
                headline="Sichtbarkeit prüfen lassen"
                sub="Sie erhalten innerhalb von 48 Stunden eine kurze Einschätzung zu Ihrem Shop und den empfohlenen nächsten Schritt."
                messagePlaceholder="Welche Seiten sollen gefunden werden, gab es schon einen Umzug oder Relaunch?"
              />
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Was Shopify SEO konkret bedeutet</h2>
            <div className="mt-6 space-y-5 text-muted-foreground">
              <p>
                Shopify bringt technisch eine gute Grundlage mit: saubere Adressen, eine
                automatische Sitemap, schnelle Server und mobile Themes. Sichtbarkeit entsteht
                dadurch aber nicht von selbst. Entscheidend ist, ob Ihre Kategorie- und
                Produktseiten die Suchanfragen abdecken, über die Menschen mit Kaufabsicht
                suchen, und ob der Shop technisch so aufgebaut ist, dass Google diese Seiten
                eindeutig zuordnen kann.
              </p>
              <p>
                Bei nahezu jedem Shop, den ich mir ansehe, liegt die größte Reserve nicht in
                zusätzlichen Blogartikeln, sondern in den Kategorieseiten. Sie sind die stärksten
                Seiten eines Shops und gleichzeitig die mit dem dünnsten Inhalt. Dazu kommen
                doppelte Adressen durch Varianten und Filter, fehlende Weiterleitungen aus alten
                Shopsystemen und eine Ladezeit, die durch Apps über die Jahre gewachsen ist.
              </p>
              <p>
                Ein Shop ohne Sichtbarkeit ist wie ein gut eingerichtetes Geschäft in einer
                Seitenstraße, an der niemand vorbeikommt. Die Einrichtung stimmt, nur weiß es
                keiner. Genau daran wird hier gearbeitet, und zwar an der Substanz, nicht an
                Kennzahlen, die sich gut vorzeigen lassen.
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Woran bei Shopify SEO gearbeitet wird</h2>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {scope.map((s) => (
                <div key={s.title} className="shadow-lift rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">
              Zehn Fehler, die in Shopify Shops Sichtbarkeit kosten
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Diese Punkte finde ich in bestehenden Shops am häufigsten. Sie lassen sich fast alle
              ohne Relaunch beheben.
            </p>
            <ol className="mt-10 grid gap-5 md:grid-cols-2">
              {mistakes.map((m, i) => (
                <li key={m.title} className="rounded-lg border border-border bg-card p-6">
                  <span className="font-mono text-xs text-primary">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 text-base font-semibold">{m.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{m.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Shopify SEO Checkliste</h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Diese Punkte gehe ich bei jedem Shop durch. Sie können damit auch selbst anfangen.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {Object.entries(checklist).map(([group, items]) => (
                <div key={group} className="rounded-lg border border-border bg-card p-6">
                  <h3 className="text-base font-semibold">{group}</h3>
                  <ul className="mt-4 space-y-3">
                    {items.map((item) => (
                      <li key={item} className="flex gap-2 text-sm text-muted-foreground">
                        <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Preise und Umfang</h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Sie sehen vorab, was eine Leistung kostet. Keine Stundenzettel ohne Rahmen, keine
              Pakete, die Sie nicht brauchen.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {packages.map((p) => (
                <div
                  key={p.name}
                  className={`rounded-lg border bg-card p-6 ${
                    p.featured ? "border-primary shadow-signal" : "border-border"
                  }`}
                >
                  <h3 className="text-lg font-semibold">{p.name}</h3>
                  <p className="text-signal mt-1 font-semibold">{p.price}</p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.text}</p>
                  <ul className="mt-4 space-y-2">
                    {p.items.map((i) => (
                      <li key={i} className="flex gap-2 text-sm text-muted-foreground">
                        <Check className="mt-0.5 size-4 shrink-0 text-primary" />
                        <span>{i}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Alle Preise netto zuzüglich Umsatzsteuer. Details zu weiteren Leistungen finden Sie
              unter{" "}
              <Link to="/preise" className="text-primary underline underline-offset-4">
                Preise
              </Link>
              .
            </p>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-4xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">
              Shopify SEO Freelancer oder SEO Agentur?
            </h2>
            <p className="mt-4 text-muted-foreground">
              Beides hat seine Berechtigung. Diese Übersicht hilft Ihnen bei der Einordnung, auch
              wenn die Entscheidung gegen mich ausfällt.
            </p>
            <div className="mt-8 overflow-hidden rounded-lg border border-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-card/60">
                  <tr>
                    <th className="p-4 font-semibold">Punkt</th>
                    <th className="p-4 font-semibold">Freelancer</th>
                    <th className="p-4 font-semibold">Agentur</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((c) => (
                    <tr key={c.point} className="border-t border-border bg-card">
                      <td className="p-4 font-medium">{c.point}</td>
                      <td className="p-4 text-muted-foreground">{c.free}</td>
                      <td className="p-4 text-muted-foreground">{c.agency}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-6 flex gap-2 text-sm text-muted-foreground">
              <X className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span>
                Wenn Sie mehrere Länder gleichzeitig, laufende Inhalte in großem Umfang und
                zusätzlich bezahlte Werbung über ein Team steuern lassen möchten, ist eine Agentur
                die passendere Wahl. Das sage ich Ihnen im Gespräch auch so.
              </span>
            </p>
          </div>
        </section>

        <CaseStrip
          slugs={["snydergolf", "kremka"]}
          title="Aus der Praxis"
          intro="Snyder Golf ist von WooCommerce auf Shopify migriert, inklusive Weiterleitungen der alten Adressen. Bei kremka ging es um Struktur, Suche und Nachbestellung im laufenden B2B-Shop."
        />

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">So läuft es ab</h2>
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((s) => (
                <div key={s.no} className="rounded-lg border border-border bg-card p-6">
                  <span className="text-signal font-display text-3xl font-bold">{s.no}</span>
                  <h3 className="mt-3 text-base font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Sichtbar werden in der KI-Suche</h2>
            <div className="mt-6 space-y-5 text-muted-foreground">
              <p>
                Immer mehr Kaufentscheidungen beginnen in ChatGPT, Perplexity oder der
                KI-Übersicht von Google. Diese Systeme zitieren Quellen, die eine Frage direkt
                beantworten. Werbetexte ohne Fakten werden übergangen.
              </p>
              <p>
                Praktisch heißt das: klare Überschriften in Frageform, kurze Antworten davor,
                Details danach. Preise, Maße, Materialien, Lieferzeiten als Fakten statt als
                Adjektive. Strukturierte Daten für Produkte und Fragen. Dazu eine maschinenlesbare
                Kurzfassung des Angebots, damit ein System Ihren Shop ohne Umwege einordnen kann.
              </p>
              <p>
                Diese Arbeit zahlt doppelt: dieselben Elemente verbessern auch die klassischen
                Suchergebnisse, weil sie die Seite verständlicher machen.
              </p>
            </div>
          </div>
        </section>

        <section className="border-b border-border/60 py-20">
          <div className="mx-auto max-w-3xl px-5">
            <h2 className="text-3xl font-bold md:text-4xl">Häufige Fragen</h2>
            <div className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border">
              {faqs.map((f) => (
                <div key={f.q} className="bg-card p-6">
                  <h3 className="text-base font-semibold">{f.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-muted-foreground">
              Hintergrund und Technik im Detail:{" "}
              <Link to="/blog/shopify-seo-optimierung-guide" className="text-primary underline underline-offset-4">
                Ratgeber Shopify SEO
              </Link>
              . Ladezeit ist Ihr Hauptproblem?{" "}
              <Link
                to="/ratgeber/shopify-pagespeed-optimieren"
                className="text-primary underline underline-offset-4"
              >
                Pagespeed optimieren
              </Link>
              .
            </p>
          </div>
        </section>

        <section id="kontakt" className="py-20 pb-28 lg:pb-20">
          <div className="mx-auto grid max-w-5xl gap-10 px-5 md:grid-cols-2 md:items-center">
            <div>
              <h2 className="text-3xl font-bold md:text-4xl">
                Sagen Sie mir, was <span className="text-signal">gefunden werden soll</span>.
              </h2>
              <p className="mt-5 text-muted-foreground">
                Sie bekommen innerhalb von 48 Stunden eine kurze Einschätzung und den
                empfohlenen nächsten Schritt. Kostenlos und unverbindlich.
              </p>
            </div>
            <LeadForm
              source="lp-shopify-seo"
              compact={false}
              headline="Shopify SEO anfragen"
              cta="Kostenlose Einschätzung erhalten"
              messagePlaceholder="Welche Seiten sollen gefunden werden, gab es schon einen Umzug oder Relaunch?"
            />
          </div>
        </section>
      </main>

      <StickyCta targetId="anfrage" />
      <GuideLinks
        links={[
          { to: "/blog/shopify-seo-optimierung-guide", label: "Shopify SEO Leitfaden", text: "Der komplette Ablauf von Technik bis Inhalt." },
          { to: "/ratgeber/shopify-pagespeed-optimieren", label: "Pagespeed verbessern", text: "Ladezeit als Ranking- und Umsatzfaktor." },
          { to: "/ratgeber/shopify-mobile-optimierung", label: "Mobile Optimierung", text: "Der Großteil der Besucher kommt vom Handy." },
          { to: "/ratgeber/shopify-produktseite-optimieren", label: "Produktseiten optimieren", text: "Titel, Details und Struktur je Produkt." },
          { to: "/ratgeber/shopify-migration", label: "Migration ohne Rankingverlust", text: "Weiterleitungen beim Umzug auf Shopify." },
          { to: "/blog/shopify-checkout-optimieren", label: "Checkout optimieren", text: "Damit der zusätzliche Traffic auch bestellt." },
        ]}
      />
      <SiteFooter />
    </div>
  );
}
